import React, { useEffect, useState } from "react"
import {
  Typography,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  Menu,
  MenuItem,
  Backdrop
} from "@mui/material"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import { useDispatch, useSelector } from "react-redux"
import AppPagination from "components/StyledComponents/AppPagination"
import TableNoRecords from "components/TableNoRecords"
import DeleteConfirmationDialog from "components/DeleteConfirmationDialog"
import AppSelect from "components/StyledComponents/AppSelect"
import { useTheme } from "@mui/system"
import { format } from "date-fns"
import { changeRedemptionStatusAction, getAllRedemptionsAction } from "redux/superAdmin/actions"

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: "12px 24px",
  "&:hover": {
    backgroundColor: theme.palette.action.hover
  }
}))

const CustomTable = styled(Table)(({ theme }) => ({
  backgroundColor: theme.palette.background.main,
  borderCollapse: "collapse !important",
  "& .MuiTableCell-root": {
    padding: "16px 24px 16px 0px",
    backgroundColor: theme.palette.background.main,
    borderBottom: "none",
    boxSizing: "border-box",
    "&:last-child": { paddingRight: 0 }
  },
  "& .MuiTableRow-root": {
    borderBottom: `2px solid #272a2c`,
    "&:last-child": { borderBottom: "none" }
  },
  "& .MuiTableCell-head": {
    verticalAlign: "bottom",
    position: "sticky",
    borderBottom: `2px solid #272a2c`,
    "&:last-child": { paddingRight: 0 }
  }
}))

const COLUMNS = [
  { id: "id", label: "#", minWidth: 60, align: "center" },
  { id: "Reward", label: "Reward", isSorting: true, sortKey: "gift_card_name", minWidth: 200 },
  { id: "Name", label: "User Name", isSorting: true, sortKey: "user_name", minWidth: 150 },
  {
    id: "Email",
    label: "Email",
    isSorting: true,
    sortKey: "user_email",
    align: "center",
    minWidth: 180
  },
  {
    id: "Redeemed At",
    label: "Redeemed At",
    isSorting: true,
    sortKey: "redeemed_at",
    align: "center",
    minWidth: 160
  },
  {
    id: "Status",
    label: "Status",
    isSorting: true,
    sortKey: "status",
    align: "center",
    minWidth: 120
  },
  { id: "Action", label: "Action", align: "center", minWidth: 80 }
]

const statusMenuItems = [
  { label: "All", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Completed", value: "completed" },
  { label: "Cancelled", value: "cancelled" }
]

const RedemptionManagement = () => {
  const { redemptions } = useSelector((state) => state.superAdmin)
  const dispatch = useDispatch()
  const theme = useTheme()
  const [currentPage, setCurrentPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState("")
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedRow, setSelectedRow] = useState(null)
  const openMenu = Boolean(anchorEl)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [targetStatus, setTargetStatus] = useState("")
  const { isLoading } = useSelector((state) => state.app)
  const filteredRedemptions = redemptions?.filter((item) => {
    if (!statusFilter) return true
    return item?.status?.toLowerCase() === statusFilter?.toLowerCase()
  })
  const { userRestaurant } = useSelector((state) => state.user)

  const itemsPerPage = 12
  const totalPages = Math.ceil((filteredRedemptions?.length || 0) / itemsPerPage)

  const paginatedRedemptions = filteredRedemptions?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  useEffect(() => {
    setCurrentPage(1)
    document.title = "Redemption Management"
    dispatch(getAllRedemptionsAction({ restaurantId: userRestaurant?.id }))
  }, [])

  const onOptionsClick = (e, row) => {
    e.stopPropagation()
    setSelectedRow(row)
    setAnchorEl(e.currentTarget)
  }

  const promptStatusChange = (status) => {
    setTargetStatus(status)
    setConfirmDialogOpen(true)
    setAnchorEl(null)
  }

  const executeStatusChange = () => {
    if (!selectedRow?.id) return

    dispatch(
      changeRedemptionStatusAction({
        redemptionId: selectedRow.id,
        status: targetStatus,
        callback: () => {
          setConfirmDialogOpen(false)
          setSelectedRow(null)
        }
      })
    )
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="body2Regular" sx={{ color: "rgba(255,255,255,0.6)" }}>
          Total Redemptions across all rewards: {redemptions?.length}
        </Typography>
        <Box sx={{ width: "300px" }}>
          <AppSelect
            label="Status"
            value={statusFilter}
            menuItems={statusMenuItems}
            onMenuClick={(item) => {
              setStatusFilter(item.value)
              setCurrentPage(1)
            }}
          />
        </Box>
      </Box>

      <Paper>
        <TableContainer>
          <CustomTable stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {COLUMNS?.map((column, index) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}>
                    <Typography variant="body2Regular" style={{ opacity: index === 0 ? 1 : 0.6 }}>
                      {column.label}{" "}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRedemptions?.length ? (
                paginatedRedemptions?.map((row, index) => {
                  return (
                    <TableRow key={row.id}>
                      <TableCell align="center">
                        <Typography variant="body2Regular">
                          {(currentPage - 1) * itemsPerPage + index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2Regular">
                          {row.gift_card?.name ?? row.gift_card_name ?? "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2Regular">{row.user?.name ?? "-"}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2Regular">{row.user?.email ?? "-"}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2Regular">
                          {row.redeemed_at
                            ? format(new Date(row.redeemed_at), "MMM d, yyyy h:mm a")
                            : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          variant="filled"
                          color={
                            row.status === "COMPLETED"
                              ? "success"
                              : row.status === "CANCELLED"
                              ? "error"
                              : "warning"
                          }
                          label={row.status}
                          size="small"
                          sx={{ fontWeight: "bold" }}
                        />
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        {row.status === "PENDING" ? (
                          <span onClick={(e) => onOptionsClick(e, row)}>
                            <MoreHorizIcon
                              sx={{ height: "24px", width: "24px", cursor: "pointer" }}
                            />
                          </span>
                        ) : (
                          <MoreHorizIcon sx={{ height: "24px", width: "24px", opacity: 0.3 }} />
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })
              ) : (
                <TableNoRecords
                  colSpan={COLUMNS.length}
                  label={
                    isLoading
                      ? "Please Wait we are getting the redemptions..."
                      : "No redemptions found"
                  }
                />
              )}
            </TableBody>
          </CustomTable>
        </TableContainer>

        <Backdrop
          sx={{ color: theme.palette.white.main, zIndex: 1300 }}
          open={openMenu}
          onClick={handleMenuClose}>
          <Menu
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                marginTop: "15px",
                backgroundColor: theme.palette.background.default,
                borderRadius: "12px",
                "& .MuiList-root": {
                  backgroundColor: theme.palette.background.default,
                  width: "150px",
                  boxSizing: "border-box",
                  padding: "10px 0"
                }
              }
            }}>
            {selectedRow?.status === "PENDING" ? (
              <>
                <StyledMenuItem onClick={() => promptStatusChange("COMPLETED")}>
                  <Typography variant="body1Regular" sx={{ color: theme.palette.green.main }}>
                    Approve
                  </Typography>
                </StyledMenuItem>
                <StyledMenuItem onClick={() => promptStatusChange("CANCELLED")}>
                  <Typography variant="body1Regular" sx={{ color: theme.palette.red.main }}>
                    Reject
                  </Typography>
                </StyledMenuItem>
              </>
            ) : (
              <StyledMenuItem disabled>
                <Typography variant="body1Regular" sx={{ opacity: 0.5 }}>
                  No Actions Available
                </Typography>
              </StyledMenuItem>
            )}
          </Menu>
        </Backdrop>
      </Paper>

      {paginatedRedemptions?.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <AppPagination
            onPageChange={setCurrentPage}
            totalPages={totalPages}
            currentPage={currentPage}
          />
        </Box>
      )}

      <DeleteConfirmationDialog
        open={confirmDialogOpen}
        title="Change Redemption Status"
        description={`Are you sure you want to mark this transaction as ${targetStatus}?`}
        btnText="Confirm"
        onClose={() => {
          setConfirmDialogOpen(false)
          setSelectedRow(null)
        }}
        onDelete={executeStatusChange}
      />
    </Box>
  )
}

export default RedemptionManagement
